import React from 'react';
import { cn } from '@/lib/utils';
import { ChatCodeBlock } from './ChatCodeBlock';
import { TopicType } from '@/types/assistant';
import { TOPIC_CONFIG } from '@/contexts/AiAssistantContext';
import { MathComponent } from './MathComponent';
import {
  EnhancedTable,
  EnhancedThead,
  EnhancedTh,
  EnhancedTd,
  EnhancedTbody,
  EnhancedTr
} from './TableComponents';
import { EnhancedBlockquote } from './EnhancedBlockquote';

type ReactMarkdownComponentProps = {
  children: React.ReactNode;
  className?: string;
};

type CodeBlockProps = ReactMarkdownComponentProps & {
  inline?: boolean;
  className?: string;
  node?: any;
};

const getLanguageFromClassName = (className?: string) => {
  if (!className) return null;
  const match = className.match(/language-(\w+)/);
  return match ? match[1] : null;
};

export const getAiMarkdownComponents = (topic: TopicType) => {
  const topicConfig = TOPIC_CONFIG[topic];

  return {
    p: ({ children }: ReactMarkdownComponentProps) => (
      <p className="mb-3 last:mb-0 text-sm leading-[1.6] whitespace-normal">
        {React.Children.toArray(children)}
      </p>
    ),
    h1: ({ children }: ReactMarkdownComponentProps) => (
      <h1 className={cn("text-base font-semibold mt-4 mb-3 whitespace-normal border-b border-white/10 pb-2 leading-tight", topicConfig.color)}>
        {React.Children.toArray(children)}
      </h1>
    ),
    h2: ({ children }: ReactMarkdownComponentProps) => (
      <h2 className={cn("text-sm font-semibold mt-4 mb-3 whitespace-normal flex items-center leading-tight", topicConfig.color)}>
        <span className="w-1.5 h-4 bg-current rounded-full mr-3"></span>
        {React.Children.toArray(children)}
      </h2>
    ),
    h3: ({ children }: ReactMarkdownComponentProps) => (
      <h3 className={cn("text-sm font-medium mt-3 mb-2 whitespace-normal leading-tight", topicConfig.color)}>
        {React.Children.toArray(children)}
      </h3>
    ),
    ul: ({ children }: ReactMarkdownComponentProps) => (
      <ul className="list-disc ml-6 my-3 space-y-1.5">
        {React.Children.toArray(children)}
      </ul>
    ),
    ol: ({ children }: ReactMarkdownComponentProps) => (
      <ol className="list-decimal ml-6 my-3 space-y-1.5">
        {React.Children.toArray(children)}
      </ol>
    ),
    li: ({ children }: ReactMarkdownComponentProps) => (
      <li className="text-sm leading-[1.6] ml-1">
        {React.Children.toArray(children)}
      </li>
    ),
    pre: ({ children }: CodeBlockProps) => (
      <div className="not-prose my-3">{children}</div>
    ),
    code: ({ children, className, inline }: CodeBlockProps) => {
      const language = getLanguageFromClassName(className);
      const codeContent = children.toString().replace(/\n$/, '');

      if (codeContent.includes('\\(') || codeContent.includes('\\[') || language === 'math') {
        return <MathComponent value={codeContent} inline={inline} />;
      }

      if (inline) {
        return (
          <code className="bg-background/60 px-1.5 py-0.5 rounded text-xs font-mono border border-white/10">
            {codeContent}
          </code>
        );
      }

      return (
        <ChatCodeBlock
          language={language || undefined}
          value={codeContent}
        />
      );
    },
    table: EnhancedTable,
    thead: EnhancedThead,
    tbody: EnhancedTbody,
    tr: EnhancedTr,
    th: EnhancedTh,
    td: EnhancedTd,
    blockquote: EnhancedBlockquote,
    hr: () => (
      <hr className="my-4 border-none h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
    ),
    strong: ({ children }: ReactMarkdownComponentProps) => (
      <strong className={cn("font-semibold", topicConfig.color)}>
        {React.Children.toArray(children)}
      </strong>
    ),
    em: ({ children }: ReactMarkdownComponentProps) => (
      <em className="italic text-muted-foreground">
        {React.Children.toArray(children)}
      </em>
    ),
  };
};

export const getUserMarkdownComponents = () => ({
  p: ({ children }: ReactMarkdownComponentProps) => (
    <p className="text-left text-sm leading-[1.6] whitespace-normal mb-3 last:mb-0">
      {React.Children.toArray(children)}
    </p>
  ),
  h1: ({ children }: ReactMarkdownComponentProps) => (
    <h1 className="text-left text-base font-semibold whitespace-normal leading-tight mb-3">
      {React.Children.toArray(children)}
    </h1>
  ),
  h2: ({ children }: ReactMarkdownComponentProps) => (
    <h2 className="text-left text-sm font-semibold whitespace-normal leading-tight mb-2">
      {React.Children.toArray(children)}
    </h2>
  ),
  ul: ({ children }: ReactMarkdownComponentProps) => (
    <ul className="list-disc ml-6 my-3 space-y-1.5">
      {React.Children.toArray(children)}
    </ul>
  ),
  ol: ({ children }: ReactMarkdownComponentProps) => (
    <ol className="list-decimal ml-6 my-3 space-y-1.5">
      {React.Children.toArray(children)}
    </ol>
  ),
  li: ({ children }: ReactMarkdownComponentProps) => (
    <li className="text-sm leading-[1.6] ml-1">
      {React.Children.toArray(children)}
    </li>
  ),
  pre: ({ children }: ReactMarkdownComponentProps) => (
    <pre className="text-left bg-background/50 p-3 rounded-md overflow-x-auto my-3 whitespace-normal text-sm">
      {children}
    </pre>
  ),
  code: ({ children, inline, className }: CodeBlockProps) => {
    const language = getLanguageFromClassName(className);
    const codeContent = children.toString().replace(/\n$/, '');

    if (inline) {
      return (
        <code className="text-left bg-background/50 px-1.5 py-0.5 rounded text-xs">{codeContent}</code>
      );
    }

    return (
      <ChatCodeBlock
        language={language || undefined}
        value={codeContent}
        className="text-left"
      />
    );
  }
});

export { MathComponent } from './MathComponent';
export { EnhancedTable, EnhancedThead, EnhancedTh, EnhancedTd, EnhancedTbody, EnhancedTr } from './TableComponents';
export { EnhancedBlockquote } from './EnhancedBlockquote';
